import { useState } from 'react';
import styles from './ToggleArchivePatientCaseModal.module.scss';
import { Button, ErrorModalContent, Modal, Text } from 'components';
import { selectModalsErrors } from 'store/app/selectors';
import { appActions } from 'store/app/slice';
import { selectCurrentPatientCase } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';

const archiveText = {
  title: 'Add to archive',
  contentTitle: 'Are you sure you want to archive the selected patient?',
  contentText:
    'The patient will be stored for reference and they can still be found in the patients list by applying appropriate filters. Admins have the authority to restore patient cases from the archive at any time',
  actionText: 'Archive',
  errorTitle: 'Archivation failed',
  errorSubtitle: 'The patient case archivation failed due to technical problems',
};

const restoreText = {
  title: 'Restore from archive',
  contentTitle: 'Are you sure you want to restore this patient from the archive?',
  contentText: "Once restored, the patient's case will be editable",
  actionText: 'Restore',
  errorTitle: 'Patient case not restored',
  errorSubtitle: 'The patient case was not restored from the archive due to technical problems',
};

export const ToggleArchivePatientCaseModal = () => {
  const dispatch = useDispatch();

  const currentPatientCase = useSelector(selectCurrentPatientCase);
  const [displayError, setDisplayError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const errorMessage = useSelector(selectModalsErrors).toggleArchivePatient;
  const errorMessageText = currentPatientCase?.isArchived
    ? `The patient case archivation failed. ${errorMessage}`
    : `'The patient case was not restored from the archive. ${errorMessage}'`;

  const handleClose = () => {
    dispatch(
      appActions.setIsModalOpened({
        name: 'archivePatient',
        isOpened: false,
      }),
    );
  };

  const onToggleHandler = async () => {
    if (!currentPatientCase) {
      handleClose();
      return;
    }
    if (isLoading) return;
    try {
      setIsLoading(true);
      await dispatch(
        patientsActions.updatePatientCase({
          patientCaseId: currentPatientCase.id,
          patientCaseData: {
            isArchived: currentPatientCase.isArchived ? false : true,
          },
        }),
      ).unwrap();

      handleClose();
      dispatch(appActions.setModalErrorText({ name: 'toggleArchivePatient', errorText: '' }));
    } catch (error: any) {
      dispatch(appActions.setModalErrorText({ name: 'toggleArchivePatient', errorText: error?.title }));
      setDisplayError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal title={currentPatientCase?.isArchived ? restoreText.title : archiveText.title} onClose={handleClose}>
      {displayError ? (
        <ErrorModalContent
          title={currentPatientCase?.isArchived ? restoreText.errorTitle : archiveText.errorTitle}
          subtitle={
            errorMessage
              ? errorMessageText
              : currentPatientCase?.isArchived
                ? restoreText.errorSubtitle
                : archiveText.errorSubtitle
          }
          onConfirm={handleClose}
        />
      ) : (
        <div className={styles.container}>
          <div className={styles.content}>
            <Text type="body-big" className={styles.contentTitle}>
              {currentPatientCase?.isArchived ? restoreText.contentTitle : archiveText.contentTitle}
            </Text>
            <Text type="body2" className={styles.contentText}>
              {currentPatientCase?.isArchived ? restoreText.contentText : archiveText.contentText}
            </Text>
          </div>
          <div className={styles.buttonRow}>
            <Button isLink onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={onToggleHandler}>
              {currentPatientCase?.isArchived ? restoreText.actionText : archiveText.actionText}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
